import { useMemo } from 'react';
import { enUS, es } from 'date-fns/locale';
import {
  ReportDateRangePreset,
  type ReportDateRangeFilter,
} from 'src/api/generated';
import { DateRangePicker } from 'src/modules/shared/components/molecules/date-range-picker';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { CalendarDateRangePickerLabels } from 'src/modules/shared/types/date-range.type';
import {
  buildReportDateRangePresets,
  formatReportDateRangePickerValue,
  resolveReportDateRange,
  toReportDateRangeFilter,
} from 'src/modules/reports/utils/report-date-range-picker';

/** Props for the Reports-specific adapter around the shared date-range picker. */
export interface ReportDateRangePickerProps {
  /** Currently applied API filter. */
  value: ReportDateRangeFilter;
  /** Called once when the user applies a valid changed range. */
  onApply: (value: ReportDateRangeFilter) => void;
  /** IANA timezone used to resolve relative report presets. */
  timezone?: string;
  /** Optional existing domain validation error. */
  errorMessage?: string | null;
  /** Whether the control is disabled. */
  disabled?: boolean;
  /** Additional class applied to the wrapper. */
  className?: string;
  /** Additional class applied to the trigger. */
  triggerClassName?: string;
}

/**
 * Adapts report API presets, translations, and timezone semantics to the
 * application-agnostic shared picker.
 */
export const ReportDateRangePicker: React.FC<ReportDateRangePickerProps> = ({
  value,
  onApply,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  errorMessage = null,
  disabled = false,
  className = '',
  triggerClassName = '',
}) => {
  const { language, t } = useI18n();
  const now = useMemo(() => new Date(), []);
  const concreteValue = useMemo(
    () => resolveReportDateRange(value, timezone, now),
    [now, timezone, value]
  );
  const presets = useMemo(
    () => buildReportDateRangePresets(timezone, t, now),
    [now, t, timezone]
  );
  const labels = useMemo<CalendarDateRangePickerLabels>(
    () => ({
      trigger: t('reports.dashboard.dateRange.trigger'),
      title: t('reports.dashboard.dateRange.title'),
      presets: t('reports.dashboard.dateRange.presets'),
      from: t('reports.dashboard.dateRange.from'),
      to: t('reports.dashboard.dateRange.to'),
      cancel: t('shared.actions.cancel'),
      close: t('reports.dashboard.dateRange.close'),
      apply: t('shared.actions.apply'),
      missingError: t('reports.dashboard.dateRange.errors.missing'),
      invalidError: t('reports.dashboard.dateRange.errors.invalid'),
      orderError: t('reports.dashboard.dateRange.errors.order'),
      beforeMinError: t('reports.dashboard.dateRange.errors.beforeMin'),
      afterMaxError: t('reports.dashboard.dateRange.errors.afterMax'),
    }),
    [t]
  );
  const selectedPresetId =
    value.preset === ReportDateRangePreset.CUSTOM_RANGE ? null : value.preset;
  const today = resolveReportDateRange(
    { preset: ReportDateRangePreset.TODAY },
    timezone,
    now
  ).to;

  return (
    <DateRangePicker
      value={concreteValue}
      selectedPresetId={selectedPresetId}
      presets={presets}
      labels={labels}
      onApply={(selection) => onApply(toReportDateRangeFilter(selection))}
      bounds={{ maxDate: today }}
      calendarLocale={language === 'es-CL' ? es : enUS}
      locale={language}
      footerNote={t('reports.dashboard.dateRange.timezone', { timezone })}
      errorMessage={errorMessage}
      disabled={disabled}
      className={className}
      triggerClassName={triggerClassName}
      formatValue={(range, presetId) =>
        formatReportDateRangePickerValue(range, presetId, language, t)
      }
    />
  );
};
